import React, {createContext, FC, PropsWithChildren, useEffect, useRef, useState} from 'react'
import {ContextState} from "../model/contextState";
import useAuth from "../hooks/useAuth";
import {Api} from "../model/api";
import {SiteResponse} from "../services";
import {useNavigate} from "react-router-dom";

const DataContext = createContext<ContextState | null>(null);

const INSTALLER_TOKEN_KEY = "INSTALLER_token";
const IS_INSTALLER_KEY = "is_INSTALLER";

export const DataProvider: FC<PropsWithChildren> = (props) => {
  const {token, logOut} = useAuth();
  const navigate = useNavigate();

  const [dataProviderInitialized, setDataProviderInitialized] = useState<boolean>(false);
  const [smartBoxes, setSmartBoxes] = React.useState<SiteResponse[]>([]);
  const [activeSmartBox, setActiveSmartBox] = useState<SiteResponse>(null);
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [liveUpdateTime, setLiveUpdateTime] = useState(null);
  const [api, setApi] = useState(new Api(null));
  const [isInstaller, setIsInstaller] = useState<boolean>(false);
  const [installerToken, setInstallerToken] = useState<string>(null);
  const [isAdvanced, setIsAdvanced] = useState<boolean>(false);

  const lastFetch = useRef(null);

  const setLastFetch = (f: any) => {
    lastFetch.current = f
  }

  const isFetching = useRef(false);
  const setIsFetching = (f: boolean) => {
    isFetching.current = f
  }

  useEffect(() => {
    if (token) {
      setApi(new Api(token));
    }
  }, [token]);

  useEffect(() => {
    if (installerToken) {
      setApi(new Api(installerToken));
    }
  }, [installerToken]);

  useEffect(() => {
    let installerToken = localStorage.getItem(INSTALLER_TOKEN_KEY);
    if (installerToken !== null) {
      setIsInstaller(true);
      setInstallerToken(installerToken)
    } else {
      // Its not correct to set isInstaller false here because it could still be an installer but not yet logged in.
      setInstallerToken(null);
    }

    let isInstaller = localStorage.getItem(IS_INSTALLER_KEY);
    if (isInstaller === "true") {
      setIsInstaller(true);
    } else {
      setIsInstaller(false);
    }

    setDataProviderInitialized(true);
  }, []);

  function storeInstallerToken(token: string): void {
    setInstallerToken(token);
    localStorage.setItem(INSTALLER_TOKEN_KEY, token);
    storeIsInstaller(true);
  }

  function storeIsInstaller(value: boolean): void {
    setIsInstaller(value);
    localStorage.setItem(IS_INSTALLER_KEY, value.toString());
  }

  function clearInstallerToken(): void {
    setInstallerToken(null);
    localStorage.removeItem(INSTALLER_TOKEN_KEY);
  }

  function clearIsInstaller(): void {
    setIsInstaller(false);
    localStorage.removeItem(IS_INSTALLER_KEY);
  }

  useEffect(() => {
    if (api) {
      api.getAxiosInstance().interceptors.response.clear()
      api.getAxiosInstance().interceptors.response.use((config) => {
        return config
      }, (error) => {
        if (error?.response && error.response?.status === 401 && error.config?.url && error.config.url.includes('token/refresh')) {
          clearInstallerToken();
          navigate("/installer/login");
        } else if (error?.response && error.response?.status === 401) {
          if (dataProviderInitialized && isInstaller) {
            const originalRequest = error.config;
            if (!originalRequest._retry) {
              originalRequest._retry = true;
              try {
                api.authenticateApi.refreshTokenApiTokenRefreshPost().then((response) => {
                  storeInstallerToken(response.data.accessToken);
                });
              } catch (e) {
                clearInstallerToken();
                navigate("/installer/login");
              }
              return api.getAxiosInstance().request(originalRequest);
            }
            return
          } else if (dataProviderInitialized && !isInstaller) {
            // We don't need to retry with the SSO salesforce login method because it should refresh automatically before the token expires.
            // If we still get a 401 we want an immediate logout with redirect to login page.
            logOut();
            navigate("/login");
          }
        }
        console.log(error);
        return
      })
    }
  }, [api, dataProviderInitialized, isInstaller]);

  return (
    <DataContext.Provider value={{
      dataProviderInitialized,
      api,
      smartBoxes,
      setSmartBoxes,
      activeSmartBox,
      setActiveSmartBox,
      data,
      setData,
      live,
      setLive,
      liveUpdateTime,
      setLiveUpdateTime,
      lastFetch,
      setLastFetch,
      isFetching,
      setIsFetching,
      isInstaller,
      storeIsInstaller,
      clearIsInstaller,
      installerToken,
      storeInstallerToken,
      clearInstallerToken,
      isAdvanced,
      setIsAdvanced
    }}>
      {props.children}
    </DataContext.Provider>
  )
}

export default DataContext;